import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Modal from '@/components/ui/Modal';
import { BannerOverlayPosition } from '@/types/ads';

type BannerPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  imageUrl?: string;
  position: BannerOverlayPosition;
  scaleFactor?: number;
  editable?: boolean;
  onScaleChange?: (value: number) => void;
};

const BannerPreviewModal = ({
  isOpen,
  onClose,
  returnFocusRef,
  imageUrl,
  position,
  scaleFactor = 1,
  editable = false,
  onScaleChange,
}: BannerPreviewModalProps) => {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerEl(node);
  }, []);
  const rafRef = useRef<number | null>(null);
  const [scale, setScale] = useState(0);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const baseWidth = 1920;
  const baseHeight = 1080;
  const bannerBaseHeight = Math.round(baseHeight * 0.0833);
  const bannerDefaultWidth = Math.round(baseWidth * 0.358);
  const overlayOffset = Math.round(baseHeight * 0.01);
  const minScale = 0.3;
  const maxScale = 3;
  const dragStateRef = useRef<{
    startScale: number;
    startX: number;
    startY: number;
  } | null>(null);

  const updateScale = useCallback(
    (container?: HTMLDivElement | null) => {
      const target = container ?? containerEl;
      if (!target) {
        return false;
      }
      const { width, height } = target.getBoundingClientRect();
      if (width === 0 || height === 0) {
        return false;
      }
      const nextScale = Math.min(width / baseWidth, height / baseHeight);
      setScale(nextScale > 0 ? nextScale : 1);
      return true;
    },
    [baseHeight, baseWidth, containerEl]
  );

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    let active = true;
    const measure = () => {
      if (!active) return;
      const ready = updateScale();
      if (!ready) {
        rafRef.current = requestAnimationFrame(measure);
      }
    };
    rafRef.current = requestAnimationFrame(measure);
    return () => {
      active = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isOpen, updateScale]);

  useEffect(() => {
    if (!isOpen || !containerEl) {
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      const handleResize = () => updateScale(containerEl);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }

    const observer = new ResizeObserver(() => updateScale(containerEl));
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, [containerEl, isOpen, updateScale]);

  const bannerAspectRatio = imageSize
    ? imageSize.width / imageSize.height
    : bannerDefaultWidth / bannerBaseHeight;
  const baseBannerWidth = Math.round(bannerBaseHeight * bannerAspectRatio);
  const baseBannerHeight = bannerBaseHeight;
  const maxBannerWidth = Math.round(baseWidth * 0.9);
  const maxScaleByWidth = maxBannerWidth / baseBannerWidth;
  const safeMaxScale = Math.min(maxScale, maxScaleByWidth);
  const appliedScale = Math.min(Math.max(scaleFactor, minScale), safeMaxScale);
  const bannerWidth = Math.round(baseBannerWidth * appliedScale);
  const bannerHeight = Math.round(baseBannerHeight * appliedScale);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Vista previa en TV"
      size="xl"
      returnFocusRef={returnFocusRef}
    >
      <div className="space-y-4">
        <div className="text-sm text-gray-500">
          Simulación del banner sobre contenido en TV. La altura es ~10% de la pantalla.
        </div>
        <div
          ref={containerRef}
          className="relative w-full aspect-video rounded-xl overflow-hidden border border-gray-200 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-inner"
        >
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              width: `${baseWidth}px`,
              height: `${baseHeight}px`,
              transform: `scale(${scale})`,
            }}
          >
            <div
              className="absolute left-1/2"
              style={{
                width: `${bannerWidth}px`,
                height: `${bannerHeight}px`,
                transform: 'translateX(-50%)',
                top: position === 'TOP' ? `${overlayOffset}px` : undefined,
                bottom: position === 'BOTTOM' ? `${overlayOffset}px` : undefined,
              }}
            >
              {imageUrl ? (
                <>
                  <img
                    src={imageUrl}
                    alt="Vista previa del banner"
                    className="h-full w-full object-cover"
                    onLoad={(event) => {
                      const img = event.currentTarget;
                      if (img.naturalWidth && img.naturalHeight) {
                        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                      }
                    }}
                  />
                  {editable && onScaleChange && (
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 h-4 w-4 rounded-full border border-white/60 bg-blue-500 shadow-sm cursor-nwse-resize"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const target = event.currentTarget;
                        target.setPointerCapture(event.pointerId);
                        dragStateRef.current = {
                          startScale: appliedScale,
                          startX: event.clientX,
                          startY: event.clientY,
                        };
                      }}
                      onPointerMove={(event) => {
                        if (!dragStateRef.current) return;
                        const { startScale, startX, startY } = dragStateRef.current;
                        const deltaX = event.clientX - startX;
                        const deltaY = event.clientY - startY;
                        const delta = (deltaX - deltaY) / 2;
                        const nextScale = startScale + delta / baseBannerWidth;
                        const clamped = Math.min(Math.max(nextScale, minScale), safeMaxScale);
                        onScaleChange(clamped);
                      }}
                      onPointerUp={(event) => {
                        if (!dragStateRef.current) return;
                        event.currentTarget.releasePointerCapture(event.pointerId);
                        dragStateRef.current = null;
                      }}
                      onPointerCancel={(event) => {
                        if (!dragStateRef.current) return;
                        event.currentTarget.releasePointerCapture(event.pointerId);
                        dragStateRef.current = null;
                      }}
                      aria-label="Ajustar tamaño del banner"
                    />
                  )}
                </>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-xs text-gray-300 bg-black/40">
                  No hay imagen seleccionada
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BannerPreviewModal;
